import React, { createContext, useContext, ReactNode } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toggleTemperatureUnit } from 'reducers/slices/temperatureSlice';

export type TemperatureUnit = 'C' | 'F';

interface TemperatureContextType {
  unit: TemperatureUnit;
  toggleUnit?: () => void;
  convertTemperature: (value: number) => string;
  convertTemperatureValueOnly: (value: number) => number; // New function to return the value only
  convertTemperatureUnitOnly: (value: number) => string; // New function to return the unit only  
}

const TemperatureContext = createContext<TemperatureContextType | undefined>(undefined);

export const TemperatureProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const dispatch = useDispatch();
  const unit = useSelector((state: any) => state.temperature?.unit || 'C');

  const toggleUnit = () => {
    dispatch(toggleTemperatureUnit());
  };

  const convertTemperature = (value: number): string => {
    if (unit === 'F') {
      const fahrenheit = Math.floor((value * 9 / 5) + 32);
      return `${fahrenheit}°F`;
    }
    return `${Math.floor(value)}°C`;
  };

  // New function that just converts the temperature value without the unit
  const convertTemperatureValueOnly = (value: number): number => {
    if (unit === 'F') {
      return Math.floor((value * 9 / 5) + 32);
    }
    return Math.floor(value);
  };

  const convertTemperatureUnitOnly = (value: number): string => {
    if (unit === 'F') {
      return `${Math.floor(value)}°F`;
    }
    return `${Math.floor(value)}°C`;
  } 

  return (
    <TemperatureContext.Provider value={{ unit, toggleUnit, convertTemperature, convertTemperatureValueOnly, convertTemperatureUnitOnly }}>
      {children}
    </TemperatureContext.Provider>
  );
};

export const useTemperature = (): TemperatureContextType => {
  const context = useContext(TemperatureContext);
  
  if (context === undefined) {
    return {
      unit: 'C',
      toggleUnit: undefined,
      convertTemperature: (value: number) => `${Math.floor(value)}°C`,
      convertTemperatureValueOnly: (value: number) => Math.floor(value),
      convertTemperatureUnitOnly: (value: number) => '°C'
    };
  }
  
  return context;
};
